import React, { useState, useEffect } from 'react';
import GenericModal from '../genericModal/GenericModal';
import { notifyError } from '../Notification';
import { faCommentDots, faPlus, faTrash, faSave } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '../Button/Button';
import './ComplaintModal.css';

const ComplaintModal = ({ show, onHide, deckId, existingComplaints = null, onSave }) => {
  const [complaints, setComplaints] = useState([
    'Unfriendly Staff',
    'Products Unavailable',
    'Long Wait Times',
    'Store Cleanliness',
  ]);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load existing complaints if provided
  useEffect(() => {
    if (existingComplaints && existingComplaints.length > 0) {
      setComplaints(existingComplaints);
    }
  }, [existingComplaints]);

  const handleComplaintChange = (index, value) => {
    const newComplaints = [...complaints];
    newComplaints[index] = value;
    setComplaints(newComplaints);
    setHasChanges(true);
  };

  const handleAddComplaint = () => {
    if (complaints.length >= 10) {
      notifyError('Maximum 10 complaints allowed.');
      return;
    }
    setComplaints([...complaints, '']);
    setHasChanges(true);
  };

  const handleRemoveComplaint = (index) => {
    if (complaints.length <= 1) {
      notifyError('At least one complaint option is required.');
      return;
    }
    const newComplaints = complaints.filter((_, i) => i !== index);
    setComplaints(newComplaints);
    setHasChanges(true);
  };

  const validateComplaints = () => {
    // Check for empty complaints
    const emptyComplaints = complaints.filter(c => !c.trim());
    if (emptyComplaints.length > 0) {
      notifyError('Please fill in all complaint options or remove empty ones.');
      return false;
    }

    // Check for duplicates
    const uniqueComplaints = new Set(complaints.map(c => c.trim().toLowerCase()));
    if (uniqueComplaints.size !== complaints.length) {
      notifyError('Duplicate complaint options found. Please use unique options.');
      return false;
    }

    return true;
  };

  const handleSave = async () => {
    if (!validateComplaints()) {
      return;
    }

    setIsSaving(true);
    try {
      // Call parent's onSave handler
      await onSave(complaints.map(c => c.trim()));
      // Success handling (notification and modal close) is done in parent
      setHasChanges(false);
    } catch (error) {
      // Error is already handled in parent, just log here
      console.error('Error in ComplaintModal:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (hasChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
        onHide();
      }
    } else {
      onHide();
    }
  };

  return (
    <GenericModal
      size='lg'
      show={show}
      onClose={handleClose}
      header={
        <div className="custom-modal-header">
          <div className="modern-preview-icon">
            <svg width="28" height="28" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
            </svg>
          </div>
          <div className="deck-modal-header-content">
            <h1 className='slide1-main-title'>Complaint Options</h1>
          </div>
        </div>
      }
      body={
        <div className="complaint-modal-container">
          {/* Instructions */}
          <div className="complaint-instructions">
            <p>
              <strong>Configure complaint options</strong> that customers can select when providing feedback.
              Each option should be clear and specific.
            </p>
          </div>

          {/* Complaints List */}
          <div className="complaints-list">
            {complaints.map((complaint, index) => (
              <div key={index} className="complaint-item">
                <div className="complaint-number">{index + 1}</div> 
                <div className="complaint-input-wrapper">
                  <input
                    type="text"
                    className="complaint-input"
                    value={complaint}
                    onChange={(e) => handleComplaintChange(index, e.target.value)}
                    placeholder={`Enter complaint option ${index + 1}`}
                    maxLength={100}
                    disabled={isSaving}
                  />
                  <span className="complaint-char-count">
                    {complaint.length}/100
                  </span>
                </div>
                <button
                  className="complaint-remove-btn"
                  onClick={() => handleRemoveComplaint(index)}
                  disabled={complaints.length === 1 || isSaving}
                  title="Remove complaint option"
                  aria-label="Remove complaint option"
                >
                  <FontAwesomeIcon icon={faTrash} />
                </button>
              </div>
            ))}
          </div>

          {/* Add Complaint Button */}
          <button
            className="complaint-add-btn"
            onClick={handleAddComplaint}
            disabled={complaints.length >= 10 || isSaving}
          >
            <FontAwesomeIcon icon={faPlus} />
            Add Complaint Option
            {complaints.length >= 10 && (
              <span className="max-reached">(Max: 10)</span>
            )}
          </button>

          {/* Info Footer */}
          <div className="complaint-info">
            <div className="info-item">
              <strong>{complaints.length}</strong> option{complaints.length !== 1 ? 's' : ''} configured
            </div>
            {hasChanges && (
              <div className="info-item unsaved">
                <span className="unsaved-indicator">●</span> Unsaved changes
              </div>
            )}
          </div>
        </div>
      }
      footer={
        <div className='complaint-modal-footer'>
          <Button variant="secondary" onClick={handleClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSave}
            disabled={isSaving || !hasChanges}
          >
            {isSaving ? (
              <>
                <FontAwesomeIcon icon={faSave} className="fa-spin" style={{ marginRight: '0.5rem' }} />
                Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} style={{ marginRight: '0.5rem' }} />
                Update
              </>
            )}
          </Button>
        </div>
      }
    />
  );
};

export default ComplaintModal;